import { ChangeEvent, ChangeEventHandler, useState } from 'react';

import { useTheme } from '@emotion/react';
import { Stack, TextField, Typography } from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import { Field } from 'formik';

interface NPITextFieldProps {
    isDisabled?: boolean;
    fieldName?: string;
    fieldType?: string;
    placeholder?: string;
    defaultValue?: string;
    handleFieldChange?: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    characterLimit?: number;
    characterLimitTextAlign?: TEXT_ALIGN_TYPE;
}

export enum TEXT_ALIGN_TYPE {
    CENTER = 'center',
    END = 'end',
    LEFT = 'left',
    RIGHT = 'right',
    START = 'start',
}

const NPITextField = ({
    isDisabled,
    fieldName,
    fieldType,
    placeholder,
    defaultValue,
    handleFieldChange,
    characterLimit,
    characterLimitTextAlign,
}: NPITextFieldProps) => {
    const theme: Theme = useTheme();
    const [value, setValue] = useState(defaultValue);

    const handleChange: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement> = (event) => {
        const inputValue: string = event.target.value;

        if (!characterLimit || inputValue.length <= characterLimit) {
            setValue(inputValue);
        }

        if (typeof handleFieldChange === 'function') {
            handleFieldChange(event);
        }
    };

    const inputStyle: SxProps<Theme> = () => {
        return {
            '& .MuiOutlinedInput-root': {
                '& fieldset': {
                    borderColor: theme.palette.border.light,
                },
                '&:hover fieldset': {
                    border: '2px solid #f3a256 !important',
                },
                '&.Mui-focused fieldset': {
                    borderColor: theme.palette.border.light,
                },
            },
            '& input[type=number]': {
                '-moz-appearance': 'textfield',
            },
            '& input[type=number]::-webkit-outer-spin-button': {
                '-webkit-appearance': 'none',
                margin: 0,
            },
            '& input[type=number]::-webkit-inner-spin-button': {
                '-webkit-appearance': 'none',
                margin: 0,
            },
        };
    };

    const labelStyle: SxProps<Theme> = () => {
        return {
            color: theme.palette.text.dark,
            fontFamily: 'inherit',
            fontWeight: 500,
            letterSpacing: 'inherit',
            paddingTop: '8px',
            textAlign: characterLimitTextAlign || TEXT_ALIGN_TYPE.LEFT,
        };
    };

    const getDisplayedText = (): string => {
        const currentLength: number = value?.length || 0;
        const charactersRemaining: number = characterLimit - currentLength;

        if (charactersRemaining === 1) {
            return '1 character remaining';
        }
        return `${charactersRemaining} characters remaining`;
    };

    return (
        <Stack width="100%">
            <Field name={fieldName}>
                {({ field }) => {
                    return (
                        <Stack width="100%" flexDirection="column">
                            <TextField
                                {...field}
                                name={undefined} // The name prop is not used and causes issues with password managers
                                value={value ?? ''}
                                defaultValue={defaultValue}
                                type={fieldType}
                                autoComplete="off"
                                disabled={isDisabled}
                                placeholder={placeholder}
                                onChange={handleChange}
                                sx={inputStyle}
                                slotProps={{
                                    htmlInput: characterLimit ? { maxLength: characterLimit } : {},
                                }}
                            />
                            {characterLimit ? (
                                <Typography sx={labelStyle}>{getDisplayedText()}</Typography>
                            ) : null}
                        </Stack>
                    );
                }}
            </Field>
        </Stack>
    );
};

export default NPITextField;
